import { createTheme } from "@mui/material/styles";

// Default brand colors - can be overridden by organization branding
const defaultBranding = {
  primary: "#5e35b1", // Purple
  primaryLight: "#9162e4",
  primaryText: "#ffffff",
  primaryLightText: "#000000",
  secondary: "#ff6f00", // Orange
  secondaryLight: "#ffad42",
  secondaryText: "#ffffff",
  secondaryLightText: "#000000",
};

/**
 * Helper function to determine if a color is light or dark
 */
const getBrightness = (hexColor) => {
  if (!hexColor || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor))
    return 128;

  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * Get appropriate text color for a background
 */
const getTextColor = (backgroundColor) => {
  return getBrightness(backgroundColor) > 140 ? "#000000" : "#ffffff";
};

/**
 * Adjust color brightness
 */
const adjustColorBrightness = (hex, percent) => {
  if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) return hex;

  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

/**
 * Generate smart color system from organization branding
 */
const generateColorSystem = (branding) => {
  const primary = branding?.primary || defaultBranding.primary;
  const secondary = branding?.secondary || defaultBranding.secondary;

  // Generate workout day colors using organization branding as base
  const workoutColors = {
    day1: branding?.primary || primary,
    day2: branding?.secondary || secondary,
    day3: branding?.primaryLight || adjustColorBrightness(primary, 40),
    day4: branding?.secondaryLight || adjustColorBrightness(secondary, 40),
    restDay: "#aaaaaa",
  };

  // Generate equipment category colors
  const equipmentColors = {
    cardio: branding?.secondary || secondary,
    weights: branding?.primary || primary,
    bodyweight: branding?.primaryLight || adjustColorBrightness(primary, 40),
    resistance:
      branding?.secondaryLight || adjustColorBrightness(secondary, 40),
    other: adjustColorBrightness(primary, -20),
  };

  return {
    workoutColors,
    equipmentColors,
    progressColor: branding?.secondary || secondary,
    successColor:
      branding?.secondaryLight || adjustColorBrightness(secondary, 40),
    cardBackground: "#ffffff",
    lightBackground: "#fafafa",
    greyBackground: "#f5f5f5",
  };
};

/**
 * Get workout day color based on index
 */
export const getWorkoutDayColor = (index, branding) => {
  const colors = generateColorSystem(branding || {});
  const colorKeys = Object.keys(colors.workoutColors);
  const colorIndex = index % (colorKeys.length - 1); // Exclude restDay
  return colors.workoutColors[colorKeys[colorIndex]];
};

/**
 * Get rest day color
 */
export const getRestDayColor = (branding) => {
  const colors = generateColorSystem(branding || {});
  return colors.workoutColors.restDay;
};

/**
 * Get grey background color for cards
 */
export const getGreyBackground = (branding) => {
  const colors = generateColorSystem(branding || {});
  return colors.greyBackground;
};

/**
 * Create custom Material-UI theme for training plans
 */
export const createTrainingPlanTheme = (branding = null) => {
  // Merge branding with defaults
  const brandColors = {
    ...defaultBranding,
    ...branding,
  };

  // Generate color system
  const colorSystem = generateColorSystem(brandColors);

  return createTheme({
    palette: {
      mode: "light",
      primary: {
        main: brandColors.primary,
        light: brandColors.primaryLight,
        contrastText:
          brandColors.primaryText || getTextColor(brandColors.primary),
      },
      secondary: {
        main: brandColors.secondary,
        light: brandColors.secondaryLight,
        contrastText:
          brandColors.secondaryText || getTextColor(brandColors.secondary),
      },
      background: {
        default: "#fafafa",
        paper: colorSystem.cardBackground,
      },
      grey: {
        50: colorSystem.lightBackground,
        100: colorSystem.greyBackground,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: "2.5rem",
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: "1.75rem",
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: "1.125rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.6,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiMenu: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiModal: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            transition: "box-shadow 0.3s ease-in-out",
            "&:hover": {
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: "1.5rem",
            "&:last-child": {
              paddingBottom: "1.5rem",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontSize: "0.8125rem",
            fontWeight: 500,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            fontSize: "0.9375rem",
            fontWeight: 600,
            padding: "0.75rem 1.5rem",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 500,
            minHeight: 48,
          },
        },
      },
    },
    // Custom properties for workout-specific colors
    customColors: colorSystem,
  });
};
